/**
 * es-US catalogue. PROVISIONAL — best-effort translation of the en-GB
 * source, written with general US-Latino Spanish conventions. Specific
 * vocabulary (e.g. "reservas" vs. "citas", formal "usted" vs. informal
 * "tú") should be validated by a native speaker before US-market launch.
 *
 * Conventions used:
 *  - Informal "tú" register (matches the friendly portal voice).
 *  - "Reservas" for bookings, "Mascotas" for pets, "Cuenta" for account.
 *  - "Pagos" for payments, "Créditos" for credits, "Paquetes" for packs.
 */

import type { TranslationMap } from '../i18n';

export const ES_US: TranslationMap = {
  common: {
    save: 'Guardar cambios',
    saving: 'Guardando…',
    saved: 'Cambios guardados.',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    loading: 'Cargando…',
    error_generic: 'Algo salió mal. Por favor, inténtalo de nuevo.',
    sign_in: 'Iniciar sesión',
    sign_out: 'Cerrar sesión',
    back: 'Atrás',
    next: 'Siguiente',
    yes: 'Sí',
    no: 'No',
    close: 'Cerrar',
    open: 'Abrir',
    optional: 'Opcional',
    required: 'Obligatorio',
  },

  nav: {
    home: 'Inicio',
    bookings: 'Reservas',
    pets: 'Mascotas',
    profile: 'Perfil',
    payments: 'Pagos',
    credits: 'Créditos',
    packs: 'Paquetes',
    notifications: 'Notificaciones',
    privacy: 'Privacidad',
    book_now: 'Reservar ahora',
    your_account: 'Tu cuenta',
  },

  account_home: {
    greeting: 'Hola {{ name }}',
    greeting_anonymous: 'Bienvenido',
    upcoming_bookings: 'Próximas reservas',
    no_upcoming: 'No hay reservas próximas — reserva una estancia o un día de guardería.',
    your_pets: 'Tus mascotas',
    no_pets: 'Aún no tenemos mascotas registradas para ti.',
    quick_actions: 'Acciones rápidas',
  },

  bookings: {
    page_title: 'Tus reservas',
    upcoming: 'Próximas',
    past: 'Pasadas',
    cancelled: 'Canceladas',
    starts: 'Inicia',
    ends: 'Termina',
    pet: 'Mascota',
    service: 'Servicio',
    total: 'Total',
    balance_due: 'Saldo pendiente',
    status: 'Estado',
    view_details: 'Ver detalles',
    cancel_booking: 'Cancelar reserva',
    request_refund: 'Solicitar reembolso',
    pay_balance: 'Pagar saldo',
    photos: 'Fotos',
    no_photos: 'Aún no hay fotos — el personal publicará novedades aquí durante la estancia.',
    booking_id: 'Número de reserva',
  },

  pets: {
    page_title: 'Tus mascotas',
    add_pet: 'Agregar una mascota',
    no_pets: 'Aún no tienes mascotas registradas.',
    breed: 'Raza',
    species: 'Especie',
    weight: 'Peso',
    date_of_birth: 'Fecha de nacimiento',
    microchip: 'Microchip',
    vaccinations: 'Vacunas',
    notes: 'Notas',
    upload_photo: 'Subir una foto',
    vet_sharing: 'Compartir con veterinario',
  },

  profile: {
    page_title: 'Perfil',
    section_eyebrow: 'Tu cuenta',
    name_label: 'Nombre',
    name_placeholder: 'Tu nombre',
    phone_label: 'Teléfono',
    email_label: 'Correo electrónico',
    email_help: 'Contáctanos para cambiar tu correo electrónico.',
    account_label: 'Cuenta:',
    language_label: 'Idioma',
    language_help: 'Elige el idioma de tu cuenta y notificaciones.',
    save_failed: 'No se pudieron guardar los cambios. Inténtalo de nuevo.',
    load_failed: 'No se pudo cargar el perfil. Inténtalo de nuevo.',
  },

  payments: {
    page_title: 'Pagos',
    history: 'Historial de pagos',
    no_payments: 'Aún no hay pagos.',
    amount: 'Monto',
    paid_on: 'Pagado el',
    method: 'Método',
    reference: 'Referencia',
    receipt: 'Recibo',
    add_payment_method: 'Agregar un método de pago',
  },

  sign_in: {
    page_title: 'Inicia sesión en {{ accountName }}',
    page_title_default: 'Iniciar sesión',
    email_label: 'Correo electrónico',
    send_link: 'Enviar enlace de inicio de sesión',
    sending: 'Enviando…',
    link_sent_title: 'Revisa tu bandeja de entrada',
    link_sent_body:
      'Te enviamos un enlace de inicio de sesión a {{ email }}. Caduca en 15 minutos.',
    consume_loading: 'Iniciando sesión…',
    consume_failed: 'No pudimos iniciar tu sesión. El enlace puede haber caducado.',
    back_to_sign_in: 'Volver al inicio de sesión',
  },
};
